import { Box, Chip, CircularProgress, Dialog, Menu, MenuItem } from '@material-ui/core';
import { ExtractorDocumentType } from '@nemuru-eng/js-api-client';
import React, { useEffect, useState } from 'react';

import { useWidth } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/hooks/useWidth';
import { ActionDialog } from '@/components/shared/components/ActionDialog';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import {
  FileEntry,
  useDocumentExtractorContext,
} from '@/components/shared/components/DocumentExtractorDialog/contexts/DocumentExtractorContext';
import { useDocumentExtractorActions } from '@/components/shared/components/DocumentExtractorDialog/hooks/useDocumentExtractorActions';
import { FlexRow } from '@/components/shared/components/FlexRow';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { IconButton } from '@/components/shared/components/IconButton';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';

const LOW_CONFIDENCE_THRESHOLD = 0.7;
const PAGE_COUNT_LIMIT = 15;

export const DOCUMENT_TYPE_KEYS: { value: ExtractorDocumentType; tId: string }[] = [
  { value: ExtractorDocumentType.DniFront, tId: 'DOCUMENT_EXTRACTOR_TYPE_DNI_FRONT' },
  { value: ExtractorDocumentType.DniBack, tId: 'DOCUMENT_EXTRACTOR_TYPE_DNI_BACK' },
  { value: ExtractorDocumentType.DniFrontBack, tId: 'DOCUMENT_EXTRACTOR_TYPE_DNI_FRONT_BACK' },
  { value: ExtractorDocumentType.NieFront, tId: 'DOCUMENT_EXTRACTOR_TYPE_NIE_FRONT' },
  { value: ExtractorDocumentType.NieBack, tId: 'DOCUMENT_EXTRACTOR_TYPE_NIE_BACK' },
  { value: ExtractorDocumentType.NieEuGreenCard, tId: 'DOCUMENT_EXTRACTOR_TYPE_NIE_EU_GREEN_CARD' },
  { value: ExtractorDocumentType.Payroll, tId: 'DOCUMENT_EXTRACTOR_TYPE_PAYROLL' },
  { value: ExtractorDocumentType.PensionRevaluation, tId: 'DOCUMENT_EXTRACTOR_TYPE_PENSION_REVALUATION' },
  { value: ExtractorDocumentType.PersonalIncomeTax, tId: 'DOCUMENT_EXTRACTOR_TYPE_PERSONAL_INCOME_TAX' },
  { value: ExtractorDocumentType.BankCertificate, tId: 'DOCUMENT_EXTRACTOR_TYPE_BANK_CERTIFICATE' },
  { value: ExtractorDocumentType.VehicleInspection, tId: 'DOCUMENT_EXTRACTOR_TYPE_VEHICLE_INSPECTION' },
];

export interface FileEntryRowProps {
  entry: FileEntry;
  index: number;
}

export const FileEntryRow = ({ entry, index }: FileEntryRowProps) => {
  const { sm } = useScreen();
  const { width, ref } = useWidth<HTMLDivElement>();
  const { palette, customBorderRadius } = useCustomTheme();
  const { getTextIdTranslation: t } = useTranslation();
  const { entries, isExtracting, dragState, duplicatedDocTypes } = useDocumentExtractorContext();
  const {
    removeEntry,
    toggleView,
    handleDocTypeChange,
    handleRetry,
    rotateEntry,
    rotatePdfEntry,
    handleRowDragStart,
    handleRowDragEnd,
    handleRowDragOver,
    handleRowDrop,
  } = useDocumentExtractorActions();

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [expandedObjectKeys, setExpandedObjectKeys] = useState<Set<string>>(() => new Set());

  const toggleObjectKey = (key: string) => {
    setExpandedObjectKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const isDraggable = entries.length > 1;
  const isDragging = dragState.source === index;
  const isDragOver = dragState.over === index && dragState.source !== index;

  const isLowConfidence =
    entry.status === 'succeeded' &&
    entry.result?.classification != null &&
    (entry.result.classification.confidence ?? 1) < LOW_CONFIDENCE_THRESHOLD;

  const isHighPageCount = entry.status === 'succeeded' && (entry.result?.fileInfo?.pageCount ?? 0) > PAGE_COUNT_LIMIT;

  const isDuplicated =
    entry.status === 'succeeded' &&
    entry.result?.documentType != null &&
    duplicatedDocTypes.has(entry.result.documentType);

  const effectiveDocType = entry.result?.documentType ?? entry.assignedDocumentType;
  const docTypeLabel = effectiveDocType
    ? t(DOCUMENT_TYPE_KEYS.find((o) => o.value === effectiveDocType)?.tId as Parameters<typeof t>[0]) ||
      effectiveDocType
    : t('DOCUMENT_EXTRACTOR_DOC_TYPE_AUTO_DETECT');

  const statusDotColor =
    entry.status === 'succeeded'
      ? palette.success?.main
      : entry.status === 'failed'
      ? palette.error.main
      : palette.grey[400];

  const hasExpandedContent = isDuplicated || entry.status === 'failed' || entry.viewExpanded;

  const canToggleExpand =
    entry.status === 'succeeded' || entry.status === 'failed' || isDuplicated || entry.status === 'pending';

  const extractedDataRecord = entry.result?.extractedData;
  const hasExtractedData =
    extractedDataRecord != null && Object.keys(extractedDataRecord as Record<string, unknown>).length > 0;

  return (
    <Box
      style={{ opacity: isDragging ? 0.4 : 1, transition: 'opacity 0.15s' }}
      onDragOver={(e) => handleRowDragOver(e, index)}
      onDrop={(e) => handleRowDrop(e, index)}
    >
      <CustomPaper
        fill="grey.light"
        style={{
          padding: 0,
          ...(isDragOver ? { outline: `2px solid ${palette.primary.main}` } : {}),
        }}
      >
        {/* Header — only this area is draggable */}
        <Box
          bgcolor={palette.common.white}
          draggable={isDraggable}
          onDragStart={() => handleRowDragStart(index)}
          onDragEnd={handleRowDragEnd}
          style={{ padding: '10px 12px', cursor: isDraggable ? 'grab' : undefined }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gridGap={16}
            bgcolor={palette.common.white}
          >
            <FlexRow color={palette.text.primary} style={{ gap: 8, flex: 1, minWidth: 0 }}>
              {isDraggable && (
                <Icon size={18} color={palette.text.secondary}>
                  drag_indicator
                </Icon>
              )}
              <Box
                title={
                  entry.status === 'succeeded'
                    ? t('DOCUMENT_EXTRACTOR_STATUS_EXTRACTED')
                    : entry.status === 'failed'
                    ? t('DOCUMENT_EXTRACTOR_STATUS_FAILED')
                    : t('DOCUMENT_EXTRACTOR_STATUS_PENDING')
                }
                style={{
                  position: 'relative',
                  width: 16,
                  height: 16,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: statusDotColor,
                  }}
                />
                {isExtracting && entry.status === 'pending' && (
                  <CircularProgress
                    size={16}
                    thickness={5}
                    style={{ position: 'absolute', top: 0, left: 0, color: palette.grey[400] }}
                  />
                )}
              </Box>
              {(isLowConfidence || isHighPageCount) && (
                <Icon size={14} color={palette.warning?.main} style={{ flexShrink: 0 }}>
                  warning
                </Icon>
              )}
              <Font
                variant="body2"
                title={entry.file.name}
                style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {entry.file.name}
              </Font>
              <Chip
                variant="outlined"
                disabled={isExtracting}
                label={
                  <Box display="flex" alignItems="center" style={{ gap: 2 }} color={palette.text.primary}>
                    <span>{docTypeLabel}</span>
                    <Icon size={12}>expand_more</Icon>
                  </Box>
                }
                size="small"
                clickable
                onClick={(e) => setMenuAnchor(e.currentTarget as HTMLElement)}
                style={{ flexShrink: 0 }}
              />
              <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                <MenuItem
                  onClick={() => {
                    handleDocTypeChange(index, undefined);
                    setMenuAnchor(null);
                  }}
                >
                  <Font variant="body2" color="textSecondary">
                    <em>{t('DOCUMENT_EXTRACTOR_DOC_TYPE_AUTO_DETECT')}</em>
                  </Font>
                </MenuItem>
                {DOCUMENT_TYPE_KEYS.map(({ value, tId }) => (
                  <MenuItem
                    key={value}
                    selected={value === effectiveDocType}
                    onClick={() => {
                      if (value === effectiveDocType) return;
                      handleDocTypeChange(index, value);
                      setMenuAnchor(null);
                    }}
                  >
                    <Font variant="body2">{t(tId as Parameters<typeof t>[0])}</Font>
                  </MenuItem>
                ))}
              </Menu>
            </FlexRow>
            <FlexRow style={{ gap: 8, flexShrink: 0 }}>
              <IconButton
                disabled={isExtracting || !canToggleExpand}
                icon={entry.viewExpanded ? 'expand_less' : 'expand_more'}
                size="small"
                onClick={() => toggleView(index)}
                iconProps={{ size: 18 }}
                aria-label={entry.viewExpanded ? 'Collapse details' : 'Expand details'}
                aria-expanded={entry.viewExpanded}
              />
              <IconButton
                icon="close"
                size="small"
                onClick={() => setConfirmRemove(true)}
                iconProps={{ size: 18 }}
                disabled={isExtracting}
                aria-label="Remove file"
              />
            </FlexRow>
          </Box>
        </Box>

        {/* Expandable content — inside the same card */}
        {hasExpandedContent && (
          <Box
            display="flex"
            flexDirection="column"
            gridGap={12}
            style={{ borderTop: `1px solid ${palette.grey[200]}`, padding: '12px' }}
          >
            {isDuplicated && (
              <CustomPaper fill="warning.light" style={{ padding: 8 }}>
                <Box display="flex" alignItems="flex-start" gridGap={8}>
                  <Icon size={16} color={palette.warning?.main}>
                    error_outline
                  </Icon>
                  <Font variant="caption" style={{ color: palette.warning?.main }}>
                    {t('DOCUMENT_EXTRACTOR_DUPLICATE_DOC_TYPE_WARNING')}
                  </Font>
                </Box>
              </CustomPaper>
            )}

            {entry.status === 'failed' && (
              <CustomPaper fill="grey.light" style={{ padding: 8 }}>
                <Box display="flex" alignItems="center" gridGap={8}>
                  <Font variant="caption" color="textSecondary">
                    {t('DOCUMENT_EXTRACTOR_EXTRACTION_FAILED_SELECT_TYPE')}
                  </Font>
                  <StandardButton main disabled={isExtracting} onClick={() => handleRetry(index)}>
                    <Text tId="RETRY" />
                  </StandardButton>
                </Box>
                {entry.retryError && (
                  <Font variant="caption" style={{ color: palette.error.main, display: 'block', marginTop: 4 }}>
                    {entry.retryError}
                  </Font>
                )}
              </CustomPaper>
            )}

            {entry.viewExpanded && (
              <div ref={ref}>
                <Box
                  display={width && width < 500 ? 'flex' : 'grid'}
                  flexDirection="column"
                  gridTemplateColumns="1fr 1fr"
                  gridGap={12}
                >
                  <Box>
                    <FilePreview
                      file={entry.file}
                      rotationDegrees={entry.rotationDegrees}
                      onRotate={() => rotateEntry(index)}
                      onRotatePdf={() => rotatePdfEntry(index)}
                    />
                  </Box>
                  <Box>
                    <CustomPaper
                      fill="paper"
                      style={{
                        padding: '12px',
                        height: sm ? 300 : 'auto',
                        overflowY: hasExtractedData ? 'auto' : 'hidden',
                      }}
                    >
                      {entry.status !== 'failed' && !hasExtractedData && (
                        <Box
                          display="flex"
                          gridGap={4}
                          flexDirection="column"
                          alignItems="center"
                          justifyContent="center"
                          height="100%"
                          minHeight={80}
                          style={{
                            borderRadius: customBorderRadius.paper,
                            border: `1px dashed ${palette.grey[300]}`,
                            backgroundColor: palette.grey[50],
                          }}
                        >
                          <Box display="flex" height={24} width={24} alignItems="center" justifyContent="center">
                            {isExtracting ? (
                              <CircularProgress size={24} style={{ color: palette.grey[400] }} />
                            ) : (
                              <Icon size={24} color={palette.grey[400]}>
                                description
                              </Icon>
                            )}
                          </Box>
                          <Font
                            variant="caption"
                            color="textSecondary"
                            style={{ textAlign: 'center', maxWidth: 280, lineHeight: 1.5 }}
                          >
                            {isExtracting
                              ? t('DOCUMENT_EXTRACTOR_EXTRACTING')
                              : t('DOCUMENT_EXTRACTOR_NO_EXTRACTED_DATA_YET')}
                          </Font>
                        </Box>
                      )}
                      {hasExtractedData && (
                        <>
                          {(isLowConfidence || isHighPageCount) && (
                            <Box display="flex" flexDirection="column" gridGap={4} mb={2}>
                              {isLowConfidence && (
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gridGap={6}
                                  style={{
                                    padding: '6px 10px',
                                    borderRadius: 6,
                                    background: `${palette.warning?.main}18`,
                                    border: `1px solid ${palette.warning?.main}40`,
                                  }}
                                >
                                  <Icon size={14} color={palette.warning?.main}>
                                    warning
                                  </Icon>
                                  <Font variant="caption" style={{ color: palette.warning?.main }}>
                                    {t('DOCUMENT_EXTRACTOR_LOW_CONFIDENCE_PREFIX')} (
                                    {Math.round((entry.result!.classification!.confidence ?? 0) * 100)}%).{' '}
                                    {t('DOCUMENT_EXTRACTOR_REVIEW_DATA')}
                                  </Font>
                                </Box>
                              )}
                              {isHighPageCount && (
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gridGap={6}
                                  style={{
                                    padding: '6px 10px',
                                    borderRadius: 6,
                                    background: `${palette.warning?.main}18`,
                                    border: `1px solid ${palette.warning?.main}40`,
                                  }}
                                >
                                  <Icon size={14} color={palette.warning?.main}>
                                    info
                                  </Icon>
                                  <Font variant="caption" style={{ color: palette.warning?.main }}>
                                    {t('DOCUMENT_EXTRACTOR_HIGH_PAGE_COUNT_PREFIX')} {entry.result!.fileInfo!.pageCount}{' '}
                                    {t('DOCUMENT_EXTRACTOR_HIGH_PAGE_COUNT_SUFFIX')}
                                  </Font>
                                </Box>
                              )}
                            </Box>
                          )}
                          {(() => {
                            const allPaths = collectObjectPaths(entry.result!.extractedData as Record<string, unknown>);
                            const allExpanded = allPaths.length > 0 && allPaths.every((p) => expandedObjectKeys.has(p));
                            return (
                              <>
                                <Box display="flex" justifyContent="space-between" alignItems="center" px={1} mb={1}>
                                  <Font variant="caption">{t('DOCUMENT_EXTRACTOR_DATA_TITLE')}</Font>
                                  {allPaths.length > 0 && (
                                    <Box
                                      component="button"
                                      onClick={() => setExpandedObjectKeys(allExpanded ? new Set() : new Set(allPaths))}
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        padding: 0,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                      }}
                                    >
                                      <Font variant="caption" color="textSecondary">
                                        {allExpanded
                                          ? t('DOCUMENT_EXTRACTOR_COLLAPSE_ALL')
                                          : t('DOCUMENT_EXTRACTOR_EXPAND_ALL')}
                                      </Font>
                                      <Icon size={14} color={palette.text.secondary}>
                                        {allExpanded ? 'unfold_less' : 'unfold_more'}
                                      </Icon>
                                    </Box>
                                  )}
                                </Box>
                                <Box
                                  component="dl"
                                  style={{
                                    margin: 0,
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 2fr',
                                    borderRadius: 6,
                                    overflow: 'hidden',
                                    border: `1px solid ${palette.grey[200]}`,
                                    borderBottom: 0,
                                  }}
                                >
                                  <DataRows
                                    entries={Object.entries(entry.result!.extractedData as Record<string, unknown>)}
                                    depth={0}
                                    expandedKeys={expandedObjectKeys}
                                    toggleKey={toggleObjectKey}
                                    palette={palette}
                                  />
                                </Box>
                              </>
                            );
                          })()}
                        </>
                      )}
                    </CustomPaper>
                  </Box>
                </Box>
              </div>
            )}
          </Box>
        )}
      </CustomPaper>

      <ActionDialog
        opened={confirmRemove}
        setOpened={setConfirmRemove}
        handleClose={() => setConfirmRemove(false)}
        Title={<>{t('DOCUMENT_EXTRACTOR_REMOVE_FILE_TITLE')}</>}
        Description={
          <>
            "{entry.file.name}" {t('DOCUMENT_EXTRACTOR_REMOVE_FILE_DESCRIPTION')}
          </>
        }
        MainButton={<Text tId="REMOVE" />}
        onSubmit={() => {
          setConfirmRemove(false);
          removeEntry(index);
        }}
        dialogProps={{ maxWidth: 'sm', disableBackdropClick: true }}
      />
    </Box>
  );
};

interface FilePreviewProps {
  file: File;
  rotationDegrees: number;
  onRotate: () => void;
  onRotatePdf: () => Promise<void>;
}

const FilePreview = ({ file, rotationDegrees, onRotate, onRotatePdf }: FilePreviewProps) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [isRotatingPdf, setIsRotatingPdf] = useState(false);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const rotateClockwise = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRotate();
  };

  const rotatePdfClockwise = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsRotatingPdf(true);
    try {
      await onRotatePdf();
    } finally {
      setIsRotatingPdf(false);
    }
  };

  if (!objectUrl) return null;

  const isPdf = file.type === 'application/pdf';

  const overlayButtonStyle: React.CSSProperties = {
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: 'rgba(0,0,0,0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  };

  const fullscreenButtonStyle: React.CSSProperties = {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  };

  return (
    <>
      <CustomPaper
        fill="paper"
        style={{
          padding: 4,
          position: 'relative',
          overflow: 'hidden',
          height: 316,
        }}
      >
        {isPdf ? (
          <iframe
            src={`${objectUrl}#toolbar=0`}
            style={{ width: '100%', height: 300, border: 'none', display: 'block' }}
            title={file.name}
          />
        ) : (
          <Box style={{ height: 300, overflowY: 'auto', overflowX: 'hidden' }}>
            <img
              src={objectUrl}
              alt={file.name}
              style={{
                width: '100%',
                height: 'auto',
                minHeight: 300,
                objectFit: 'contain',
                display: 'block',
                transform: `rotate(${rotationDegrees}deg)`,
                transformOrigin: 'center center',
                transition: 'transform 0.2s ease',
              }}
            />
          </Box>
        )}
        <Box
          style={{
            position: 'absolute',
            top: 6,
            right: 6,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            backgroundColor: 'rgba(0,0,0,0.25)',
            borderRadius: '24px',
            padding: 4,
            zIndex: 1,
          }}
        >
          <Box aria-label="Open fullscreen" onClick={() => setFullscreen(true)} style={overlayButtonStyle}>
            <Icon size={16} color="#fff">
              open_in_full
            </Icon>
          </Box>
          {isPdf ? (
            <Box
              aria-label="Rotate PDF clockwise"
              onClick={isRotatingPdf ? undefined : rotatePdfClockwise}
              style={{
                ...overlayButtonStyle,
                opacity: isRotatingPdf ? 0.6 : 1,
                cursor: isRotatingPdf ? 'default' : 'pointer',
              }}
            >
              {isRotatingPdf ? (
                <CircularProgress size={12} style={{ color: '#fff' }} />
              ) : (
                <Icon size={16} color="#fff">
                  rotate_right
                </Icon>
              )}
            </Box>
          ) : (
            <Box aria-label="Rotate clockwise" onClick={rotateClockwise} style={overlayButtonStyle}>
              <Icon size={16} color="#fff">
                rotate_right
              </Icon>
            </Box>
          )}
        </Box>
      </CustomPaper>

      <Dialog open={fullscreen} onClose={() => setFullscreen(false)} maxWidth={false} fullScreen>
        <Box
          style={{ position: 'relative', width: '100%', height: '100%', background: '#000' }}
          onClick={() => setFullscreen(false)}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              backgroundColor: 'rgba(0,0,0,0.45)',
              borderRadius: '24px',
              padding: 6,
            }}
          >
            <Box aria-label="Close fullscreen" onClick={() => setFullscreen(false)} style={fullscreenButtonStyle}>
              <Icon size={20} color="#fff">
                close_fullscreen
              </Icon>
            </Box>
            {isPdf ? (
              <Box
                aria-label="Rotate PDF clockwise"
                onClick={isRotatingPdf ? undefined : rotatePdfClockwise}
                style={{
                  ...fullscreenButtonStyle,
                  opacity: isRotatingPdf ? 0.6 : 1,
                  cursor: isRotatingPdf ? 'default' : 'pointer',
                }}
              >
                {isRotatingPdf ? (
                  <CircularProgress size={16} style={{ color: '#fff' }} />
                ) : (
                  <Icon size={20} color="#fff">
                    rotate_right
                  </Icon>
                )}
              </Box>
            ) : (
              <Box aria-label="Rotate clockwise" onClick={rotateClockwise} style={fullscreenButtonStyle}>
                <Icon size={20} color="#fff">
                  rotate_right
                </Icon>
              </Box>
            )}
          </Box>
          {isPdf ? (
            <iframe
              src={`${objectUrl}#toolbar=0`}
              style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
              title={file.name}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <img
              src={objectUrl}
              alt={file.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block',
                transform: `rotate(${rotationDegrees}deg)`,
                transformOrigin: 'center center',
                transition: 'transform 0.2s ease',
              }}
            />
          )}
        </Box>
      </Dialog>
    </>
  );
};

const collectObjectPaths = (data: Record<string, unknown>, keyPath = ''): string[] => {
  const paths: string[] = [];
  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined || value instanceof Date) continue;
    if (typeof value === 'object') {
      const fullKey = keyPath ? `${keyPath}.${key}` : key;
      paths.push(fullKey);
      const nested = Array.isArray(value)
        ? Object.fromEntries((value as unknown[]).map((v, i) => [String(i + 1), v]))
        : (value as Record<string, unknown>);
      paths.push(...collectObjectPaths(nested, fullKey));
    }
  }
  return paths;
};

interface DataRowsProps {
  entries: [string, unknown][];
  depth: number;
  expandedKeys: Set<string>;
  toggleKey: (key: string) => void;
  palette: ReturnType<typeof useCustomTheme>['palette'];
  keyPath?: string;
}

const DataRows = ({ entries, depth, expandedKeys, toggleKey, palette, keyPath = '' }: DataRowsProps) => {
  const border = `1px solid ${palette.grey[200]}`;
  const indent = 10 + depth * 16;

  return (
    <>
      {entries
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => {
          const fullKey = keyPath ? `${keyPath}.${key}` : key;

          if (value === null) {
            return (
              <React.Fragment key={fullKey}>
                <Box component="dt" style={{ padding: `5px 10px 5px ${indent}px`, borderBottom: border, margin: 0 }}>
                  <Font variant="caption" color="textSecondary">
                    {formatFieldKey(key)}
                  </Font>
                </Box>
                <Box component="dd" style={{ padding: '5px 10px', borderBottom: border, margin: 0 }}>
                  <Font variant="caption" style={{ color: palette.grey[400], fontStyle: 'italic' }}>
                    null
                  </Font>
                </Box>
              </React.Fragment>
            );
          }

          if (typeof value === 'object' && !(value instanceof Date)) {
            const isExpanded = expandedKeys.has(fullKey);
            const nestedEntries = Array.isArray(value)
              ? (value as unknown[]).map((v, idx) => [String(idx + 1), v] as [string, unknown])
              : Object.entries(value as Record<string, unknown>);

            return (
              <React.Fragment key={fullKey}>
                <Box
                  component="dt"
                  onClick={() => toggleKey(fullKey)}
                  style={{
                    gridColumn: '1 / -1',
                    padding: `5px 10px 5px ${indent}px`,
                    background: palette.grey[100],
                    borderBottom: border,
                    margin: 0,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <Icon size={14} color={palette.text.secondary}>
                    {isExpanded ? 'expand_less' : 'expand_more'}
                  </Icon>
                  <Font variant="caption" color="textSecondary">
                    {formatFieldKey(key)}
                  </Font>
                </Box>
                {isExpanded && (
                  <DataRows
                    entries={nestedEntries}
                    depth={depth + 1}
                    expandedKeys={expandedKeys}
                    toggleKey={toggleKey}
                    palette={palette}
                    keyPath={fullKey}
                  />
                )}
              </React.Fragment>
            );
          }

          const displayValue = value instanceof Date ? value.toLocaleDateString() : String(value);
          return (
            <React.Fragment key={fullKey}>
              <Box component="dt" style={{ padding: `5px 10px 5px ${indent}px`, borderBottom: border, margin: 0 }}>
                <Font variant="caption" color="textSecondary" className="overflow-text-ellipsis">
                  {formatFieldKey(key)}
                </Font>
              </Box>
              <Box
                component="dd"
                title={displayValue}
                style={{
                  padding: '5px 10px',
                  borderBottom: border,
                  margin: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                <Font variant="caption">{displayValue}</Font>
              </Box>
            </React.Fragment>
          );
        })}
    </>
  );
};

const formatFieldKey = (key: string) =>
  key
    .replace(/([A-Z])/g, ' $1')
    .toLowerCase()
    .replace(/^./, (s) => s.toUpperCase());
